import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";

// Replaces useTodos.js — "My Tasks" data (personal + assigned-to-me), against
// the Phase-1 backend contract (title/description/status/priority/dueDate/
// assignedBy/assignedByName). See TASK_ASSIGNMENT_FEATURE_PLAN.md §4.1 and
// WEB_TASK_ASSIGNMENT_UI_PLAN.md. The route/response key (`todos`) is
// unchanged from before — only the shape of each row is richer now.
const useTasks = (BASE_URL, currentInfinityCode) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!currentInfinityCode) return;
    setLoading(true);
    try {
      const response = await apiFetch(`${BASE_URL}/todos/${currentInfinityCode}`, {
        method: "GET",
      });
      const data = await response.json();
      setTasks(data.todos || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, currentInfinityCode]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Creates a personal task (assignedTo omitted/empty) OR an assignment to a
  // direct report (assignedTo set) — the server derives the OWNER from the
  // auth token / the validated direct-report relationship either way (plan
  // §4.3); `assignedTo` here is just telling the server which of those two
  // paths to take, never who "owns" the request.
  //
  // BUGFIX: this previously destructured a fixed field list that excluded
  // `assignedTo`, so it was silently dropped before the request even went
  // out — every task created from the form was submitted as personal
  // regardless of whether an assignee was picked, which is why "Assigned To"
  // never showed up anywhere afterwards (assignedBy was never actually set).
  const addTask = async ({ title, description, category, priority, dueDate, assignedTo }) => {
    const response = await apiFetch(`${BASE_URL}/todos`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, description, category, priority, dueDate, assignedTo }),
    });
    const data = await response.json();
    if (!response.ok || data.success === false) {
      throw new Error(data.error || "Failed to create task");
    }
    // Only a PERSONAL task belongs on this list — an assignment's owner is
    // the assignee, not the manager who created it, so it must never be
    // prepended to the manager's own "My Tasks" state (that list stays
    // exactly what the server would return for the manager's own code).
    if (!assignedTo) {
      setTasks((prev) => [data.todo, ...prev]);
    }
    return data.todo;
  };

  // Owner path — Start / Complete only (server enforces this split, plan §4.4).
  const setTaskStatus = async (id, status) => {
    const response = await apiFetch(`${BASE_URL}/todos/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    if (!response.ok || data.success === false) {
      throw new Error(data.error || "Failed to update task");
    }
    setTasks((prev) => prev.map((t) => (t.id === id ? data.todo : t)));
    return data.todo;
  };

  // Assigner path — edit title/description/priority/dueDate, or cancel
  // (status: "CANCELLED"). Server rejects anything the caller isn't allowed
  // to do, this is just the single call both flows share.
  const updateTask = async (id, patch) => {
    const response = await apiFetch(`${BASE_URL}/todos/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    const data = await response.json();
    if (!response.ok || data.success === false) {
      throw new Error(data.error || "Failed to update task");
    }
    setTasks((prev) => prev.map((t) => (t.id === id ? data.todo : t)));
    return data.todo;
  };

  // Personal (non-assigned) tasks only — server 403s anything else (plan §4.5).
  const deleteTask = async (id) => {
    const response = await apiFetch(`${BASE_URL}/todos/${id}`, { method: "DELETE" });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.error || "Failed to delete task");
    }
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  return { tasks, setTasks, loading, fetchTasks, addTask, setTaskStatus, updateTask, deleteTask };
};

export default useTasks;
